<template>
  <b-overlay
    :show="isProcessing"
    variant="white"
    opacity="0.5"
    class="drv-overlay"
    spinner-small
  >
    <template #overlay>
      <b-icon icon="three-dots" animation="cylon" font-scale="2"></b-icon>
    </template>

    <b-card
      :style="cssVars"
      body-class="overflow-auto"
      class="drv bg-white mb-0"
    >
      <DrvColumnsModal
        ref="columnsModal"
        @toggle-columns="onToggleColumns"
        :fields="fields"
      />

      <slot name="header">
        <template slot="header">
          <div class="d-flex justify-content-end d-lg-none">
            <b-link
              :class="collapseNav ? null : 'collapsed'"
              :aria-expanded="collapseNav ? 'true' : 'false'"
              aria-controls="drv-table-nav"
              @click="collapseNav = !collapseNav"
            >
              <b-icon icon="list" font-scale="2.25"></b-icon>
            </b-link>
          </div>
          <b-collapse
            v-model="collapseNav"
            :class="{
              'd-block d-lg-flex justify-content-sm-between flex-wrap':
                collapseNav
            }"
            id="drv-table-nav"
          >
            <slot name="header-left"></slot>

            <!-- <DrvSearchBar
							ref="searchBar"
							v-model="searchValue"
							@set-key="(payload) => handleSearch(payload, 'key')"
							:fields="fields"
							class="mb-3 mb-lg-0 mr-lg-3 my-lg-1"
						/> -->

            <DrvFilters
              ref="filters"
              v-model="filterParameters"
              @filter="onFilter"
              :filters="filters"
              class="mr-2 ml-auto my-lg-1 d-inline-block"
            />

            <b-btn
              @click.stop="$refs.columnsModal.$emit('show', true)"
              variant="primary"
              class="drv-btn my-lg-1"
              pill
              >Columns</b-btn
            >

            <slot name="header-right"></slot>
          </b-collapse>
        </template>
      </slot>

      <DrvFilterBadges @remove-filter="onRemoveFilter" :filters="cpBadges" />

      <DrvTable
        ref="table"
        @sort="onSort"
        :columns="cpColumns"
        :items="cpItems"
      />

      <slot name="footer">
        <template slot="footer">
          <DrvPagination
            ref="pagination"
            @paginate="onPaginate"
            :page="currentPage"
            :page-size="currentPageSize"
            :total-pages="totalPages"
            :total-items="items.length"
          />
        </template>
      </slot>
    </b-card>
  </b-overlay>
</template>

<script>
import axios from '@util/axios'

import { getUniqueID } from './../utils/arrays'
import { DEFAULT_SORT_TYPE } from './../utils/constants'

import DrvColumnsModal from './DrvColumnsModal.vue'
import DrvFilters from './DrvFilters.vue'
import DrvFilterBadges from './DrvFilterBadges.vue'
import DrvPagination from './DrvPagination.vue'
import DrvSearchBar from './DrvSearchBar.vue'
import DrvTable from './DrvTable.vue'

export default {
  name: 'datarows-viewer',
  props: {
    apiUrl: {
      type: String,
      required: true,
      description: 'Api endpoint url.'
    },
    apiHeaders: {
      type: Object,
      description: 'Api custom request headers.'
    },
    apiExtraPayloads: {
      type: Object,
      description: 'Api extra payload datas',
      default() {
        return {}
      }
    },
    fields: {
      type: Array,
      description:
        'Field names or object field definitions array. Object field definitions array should have `key` or `label`.',
      validator: function (value) {
        if (!Array.isArray(value)) return false

        if (!value.length) return true

        if ('string' !== typeof value[0]) {
          const objKeys = Object.keys(value[0])

          return 0 <= objKeys.indexOf('key') || 0 <= objKeys.indexOf('label')
        } else {
          return true
        }
      },
      required: true
    },
    filters: {
      type: Array,
      description: '',
      default: () => [],
      validator: function (value) {
        if (!value.length) return true

        const objKeys = Object.keys(value[0])

        return objKeys.includes('name', 'options')
      }
    },
    pageSize: {
      type: Number,
      default: 10
    },
    colors: {
      type: Object,
      default() {
        return {
          primary: '#1d434b',
          accent: '#e59616'
        }
      }
    }
  },
  components: {
    DrvColumnsModal,
    DrvFilters,
    DrvFilterBadges,
    DrvPagination,
    DrvSearchBar,
    DrvTable
  },
  data() {
    return {
      collapseNav: true,
      isProcessing: true,
      items: [],
      toggledColumns: [],

      // Searching
      searchKey: null,
      searchValue: null,
      searchList: [],

      // Filtering
      filterParameters: {},

      // Sorting
      sortKey: '',
      sortOrder: DEFAULT_SORT_TYPE,

      // Pagination
      currentPage: 1,
      currentPageSize: 10,
      totalPages: 0,
      prevPage: null,
      nextPage: null,
      windowWidth: window.innerWidth
    }
  },
  computed: {
    cssVars() {
      return {
        '--drv-primary': this.colors.primary || '#1d434b',
        '--drv-accent': this.colors.accent || '#1c4a50'
      }
    },
    cpQuery() {
      const filterParameters = Object.entries(this.filterParameters)

      const queryParams = {
        search: this.searchList,
        sort: this.sortKey
          ? {
              field: this.sortKey,
              operator: this.sortOrder
            }
          : null
      }

      if (filterParameters.length)
        queryParams.parameters = filterParameters
          .filter((item) => {
            return item[1] ? true : false
          })
          .map((item) => {
            let data = {
              name: item[0],
              value: Array.isArray(item) ? item[1] : item
            }

            if (!Array.isArray(data.value) && data.value.includes(','))
              data.type = 'Range'

            const idx = this.searchList.findIndex(
              (sitem) => sitem.name === data.name
            )

            if (0 <= idx)
              data.value = data.value.filter((vitem) => {
                return 0 <= this.searchList[idx].value.indexOf(vitem)
                  ? false
                  : true
              })

            return data
          })
          .filter((item) => item.value.length)

      return queryParams
    },
    cpParams() {
      return {
        page: this.currentPage,
        page_size: this.currentPageSize
      }
    },
    cpColumns() {
      return this.fields
        .filter((item) => {
          const key = 'string' === typeof item ? item : item.key

          return 0 <= this.toggledColumns.indexOf(key)
        })
        .map((item) => {
          return {
            uniqueId: getUniqueID(`field-${item}`),
            key: 'string' === typeof item ? item : item.key,
            label:
              'string' === typeof item
                ? item.replace(/_/g, ' ')
                : item.label || item.key,
            width: item.width ? `${item.width}px` : 'auto',
            sortable: item.sortable || false,
            decimals: item.decimals || null
          }
        })
    },
    cpItems() {
      const items = this.items

      return items.map((item) => {
        this.fields
          .filter((fitem) =>
            'string' === typeof fitem || !fitem.formatter ? false : true
          )
          .forEach((fitem) => {
            item[fitem.key] = fitem.formatter(item[fitem.key])
          })

        return item
      })
    },
    cpBadges() {
      const badges = this.filterParameters

      if (this.searchList.length)
        this.searchList.forEach((item) => {
          if (badges[item.name]) {
            const searchList = item.value.filter((vitem) => {
              return 0 > badges[item.name].indexOf(vitem)
            })

            this.$set(badges, item.name, badges[item.name].concat(searchList))
          } else {
            this.$set(badges, item.name, item.value)
          }
        })

      return badges
    }
  },
  methods: {
    async getItems() {
      if (!this.apiUrl) return false

      this.isProcessing = true

      const rs = await axios({
        url: this.apiUrl,
        method: 'POST',
        headers: this.apiHeaders || null,
        data: {
          ...this.apiExtraPayloads,
          ...this.cpQuery
        },
        params: this.cpParams
      })

      this.isProcessing = false

      this.items = rs.data.data.results

      this.totalPages = rs.data.data.count
      this.nextPage = rs.data.data.next
      this.prevPage = rs.data.data.previous
    },
    handleSearch(value, type = 'search') {
      if ('key' === type) this.searchKey = value

      if (('search' === type && !this.searchKey) || !value) return
      else if ('key' === type && !this.searchValue) return

      this.currentPage = 1

      const searchIndex = this.searchList.findIndex(
        (item) => item.name === this.searchKey
      )

      if (0 <= searchIndex)
        this.searchList[searchIndex].value.push(this.searchValue)
      else
        this.searchList.push({
          name: this.searchKey,
          value: [this.searchValue]
        })
    },
    onToggleColumns(payload) {
      this.toggledColumns = payload
    },
    onSort(payload) {
      this.sortKey = payload.key
      this.sortOrder = payload.order
    },
    onPaginate(payload) {
      this.currentPage = payload.page
      this.currentPageSize = payload.pageSize
    },
    onFilter(payload) {
      this.$set(this.filterParameters, payload.name, payload.value)
    },
    onRemoveFilter(payload) {
      if ('*' === payload) {
        this.searchList = []

        this.$set(this, 'filterParameters', {})
      } else {
        const searchIndex = this.searchList.findIndex(
          (item) => item.name === payload.name
        )

        if (0 <= searchIndex) {
          const valueIndex = this.searchList[searchIndex].value.indexOf(
            payload.value
          )

          if (0 <= valueIndex)
            this.searchList[searchIndex].value.splice(valueIndex, 1)

          if (!this.searchList[searchIndex].value.length)
            this.searchList.splice(searchIndex, 1)
        }

        const idx = this.filterParameters[payload.name]
          ? this.filterParameters[payload.name].indexOf(payload.value)
          : -1

        if (0 <= idx) {
          if (Array.isArray(this.filterParameters[payload.name]))
            this.filterParameters[payload.name].splice(idx, 1)
          else this.filterParameters[payload.name] = []
        }
      }
    }
  },
  watch: {
    cpQuery: {
      deep: true,
      handler() {
        this.getItems()
      }
    },
    cpParams: {
      deep: true,
      handler() {
        this.getItems()
      }
    },
    windowWidth: {
      immediate: true,
      handler(newVal) {
        this.collapseNav = newVal <= 991 ? false : true
      }
    },
    searchValue(newValue) {
      this.handleSearch(newValue, 'search')
    }
  },
  mounted() {
    if (this.pageSize) this.currentPageSize = this.pageSize

    this.$on('processing', (payload) => {
      this.isProcessing = payload
    })

    this.$bus.$on('error-catched', () => {
      this.isProcessing = false
    })

    this.getItems()
  }
}
</script>

<style lang="scss">
@import './../styles/style';
</style>
